/**
 * עסקאות — "אני רוצה למצוא עסקה, להבין לאן הלך הכסף ולתקן קטגוריה שגויה" (spec §4.2).
 *
 * Filters live in the URL (deep links, spec §3.3): account · cat · from/to · q · dir · status ·
 * kind · sort · tx. Desktop renders a table grouped by day, mobile a list with swipe actions;
 * both share the same rows. Rows that are not "flow" rows (internal, card-charge, returns) are
 * listed in grey with a chip and excluded from the summary and the day totals.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent, ReactNode } from 'react';
import type { GroupKey, SummaryResponse, TransactionDetail, TransactionRow, TransactionsQuery, TransactionsResponse } from '../../../shared-types.ts';
import { GROUP_KEYS } from '../../../shared-types.ts';
import { dayName, fmtDM, fmtDate, fmtForeign, fmtMoney } from '../format.ts';
import { CATEGORY, he, plural } from '../i18n/he.ts';
import { Icon } from '../components/Icon.tsx';
import { Chip, EmptyState, Ltr } from '../components/ui.tsx';
import { Amount, CategoryChip, ForeignLine, PendingChip, TxIcon, TxProvider } from '../components/tx.tsx';
import { PeriodChip } from '../components/Shell.tsx';
import { subscribeInvalidate } from '../hooks/cache.ts';
import { useApp } from '../hooks/useApp.tsx';
import { useIsMobile } from '../hooks/useMediaQuery.ts';
import { useRouter } from '../hooks/useRouter.tsx';
import { buildPath } from '../router.ts';
import { errorMessage } from '../api-select.ts';

function queryFromRoute(query: Readonly<Record<string, string>>, period: string): TransactionsQuery {
  const out: TransactionsQuery = { period: period as TransactionsQuery['period'] };
  if (query.account) out.account = query.account;
  if (query.cat) out.cat = query.cat;
  if (query.q) out.q = query.q;
  if (query.dir === 'credit' || query.dir === 'debit') out.dir = query.dir;
  if (query.status === 'pending' || query.status === 'booked') out.status = query.status;
  if (query.kind) out.kind = query.kind as TransactionsQuery['kind'];
  if (query.sort === 'amount') out.sort = 'amount';
  if (query.from && query.to) {
    out.from = query.from;
    out.to = query.to;
  }
  return out;
}

function activeFilterCount(query: Readonly<Record<string, string>>): number {
  return ['account', 'cat', 'q', 'dir', 'status', 'kind'].filter((key) => query[key]).length + (query.from && query.to ? 1 : 0);
}

export function TransactionsScreen({ summary }: { summary: SummaryResponse | undefined }) {
  const { api, period, toast } = useApp();
  const { route, navigate } = useRouter();
  const [pages, setPages] = useState<TransactionsResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [openTx, setOpenTx] = useState<TransactionDetail | null>(null);
  const moreRef = useRef<HTMLDivElement | null>(null);

  const query = queryFromRoute(route.query, period);
  const key = JSON.stringify(query);

  const load = useCallback(
    (cursor?: string) => {
      setLoading(!cursor);
      api.transactions({ ...query, cursor }).then(
        (response) => {
          setPages((prev) => (cursor ? [...prev, response] : [response]));
          setLoading(false);
          setError(null);
        },
        (err: unknown) => {
          setLoading(false);
          setError(err);
        },
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [api, key],
  );

  useEffect(() => {
    setPages([]);
    load();
  }, [load]);

  /**
   * This screen pages by cursor, so it holds its own `pages` state instead of `useQuery` — which
   * also means nothing re-read it when the data changed underneath. Editing a category left the
   * edited row showing its old chip until the user navigated away, even though the server had
   * already emitted `data:changed {scopes:['summary','transactions','categories']}`. Subscribing to
   * the same bus every `useQuery` listens on (SSE, sync completion, our own mutations) reloads the
   * list from its first page, keeping the current render on screen while it does.
   */
  useEffect(
    () =>
      subscribeInvalidate((scopes) => {
        if (scopes.includes('transactions')) load();
      }),
    [load],
  );

  // deep link ?tx=<id> opens the detail panel
  useEffect(() => {
    const id = route.query.tx;
    if (!id) {
      setOpenTx(null);
      return;
    }
    api.transaction(id).then(setOpenTx, () => setOpenTx(null));
  }, [api, route.query.tx]);

  const first = pages[0];
  const items = pages.flatMap((page) => page.items);
  const nextCursor = pages[pages.length - 1]?.nextCursor ?? null;
  const dayTotals = new Map(pages.flatMap((page) => page.dayTotals).map((total) => [total.day, total]));

  useEffect(() => {
    const node = moreRef.current;
    if (!node || !nextCursor) return undefined;
    const observer = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting) {
        observer.disconnect();
        load(nextCursor);
      }
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [nextCursor, load]);

  const setFilter = (patch: Record<string, string | null>) => {
    const next: Record<string, string> = { ...route.query };
    for (const [k, v] of Object.entries(patch)) {
      if (v === null || v === '') delete next[k];
      else next[k] = v;
    }
    navigate(buildPath('transactions', null, next), { replace: true });
  };

  const openDetail = (id: string) => setFilter({ tx: id });
  const closeDetail = () => setFilter({ tx: null });

  const filters = activeFilterCount(route.query);

  return (
    <div className="screen">
      <PeriodChip summary={summary} inline />
      <Filters query={route.query} setFilter={setFilter} count={filters} summary={summary} />
      {loading && !first ? (
        <div className="sec" style={{ border: 0 }}>
          <div className="sk" style={{ height: '320px' }} />
        </div>
      ) : error && !first ? (
        <div className="sec" style={{ border: 0 }}>
          <EmptyState
            icon="alert"
            title={he.states.loadFailed}
            body={errorMessage(error)}
            action={
              <button type="button" className="btn sm" onClick={() => load()}>
                {he.states.tryAgain}
              </button>
            }
          />
        </div>
      ) : first && items.length ? (
        <>
          <SummaryRow response={first} query={route.query} setFilter={setFilter} />
          <div className="tblscroll">
            <table className="txtable">
              <thead>
                <tr>
                  <th scope="col">{he.tx.colDate}</th>
                  <th scope="col">{he.tx.colMerchant}</th>
                  <th scope="col">{he.tx.colCategory}</th>
                  <th scope="col">{he.tx.colInstallments}</th>
                  <th scope="col" className="amt" aria-sort={route.query.sort === 'amount' ? 'descending' : 'none'}>
                    <button type="button" aria-label={he.tx.sortByAmount} onClick={() => setFilter({ sort: route.query.sort === 'amount' ? null : 'amount' })}>
                      {he.tx.colAmount}
                      <Icon name={route.query.sort === 'amount' ? 'sortD' : 'sortU'} className="xs" />
                    </button>
                  </th>
                  <th scope="col">{he.tx.colStatus}</th>
                </tr>
              </thead>
              <tbody>
                <TableRows rows={items} dayTotals={dayTotals} openId={route.query.tx} onOpen={openDetail} />
              </tbody>
            </table>
          </div>
          <div className="txlist">
            <ListRows rows={items} dayTotals={dayTotals} onOpen={openDetail} />
          </div>
          {nextCursor ? (
            <div ref={moreRef} className="row" style={{ justifyContent: 'center', padding: '12px' }} aria-live="polite">
              <button type="button" className="btn ghost" onClick={() => load(nextCursor)}>
                {he.tx.loadMore}
              </button>
            </div>
          ) : null}
        </>
      ) : (
        <div className="sec" style={{ border: 0 }}>
          <EmptyState
            title={filters ? he.tx.emptyFiltered : he.states.emptyPeriodTitle}
            body={he.states.emptyPeriodBody}
            action={
              filters ? (
                <button type="button" className="btn sm" onClick={() => navigate(buildPath('transactions'), { replace: true })}>
                  {he.tx.clearFilter}
                </button>
              ) : undefined
            }
          />
        </div>
      )}
      {openTx ? (
        <DetailPanel
          detail={openTx}
          onClose={closeDetail}
          onChanged={(next) => {
            setOpenTx(next);
            load();
            toast(he.tx.noteSaved);
          }}
        />
      ) : null}
    </div>
  );
}

function Filters({
  query,
  setFilter,
  count,
  summary,
}: {
  query: Readonly<Record<string, string>>;
  setFilter: (patch: Record<string, string | null>) => void;
  count: number;
  summary: SummaryResponse | undefined;
}) {
  const { openMenu, openSheet, closeLayer } = useApp();
  const searchTimer = useRef<number | undefined>(undefined);
  const cats = query.cat ? query.cat.split(',') : [];
  const accounts = (summary?.accounts ?? []).filter((row) => row.account.type === 'CHECKING' || row.account.type === 'CARD');
  return (
    <div className="filters">
      <div className="selwrap only-d">
        <select className="select" aria-label={he.tx.account} value={query.account ?? ''} onChange={(event) => setFilter({ account: event.target.value || null })}>
          <option value="">{he.tx.allAccounts}</option>
          {accounts.map((row) => (
            <option key={row.account.id} value={row.account.id}>
              {row.account.name} {row.account.numberLast4 ? `****${row.account.numberLast4}` : ''}
            </option>
          ))}
        </select>
        <Icon name="chevD" className="xs" />
      </div>
      <button
        type="button"
        className="pill only-d"
        aria-haspopup="menu"
        onClick={(event) =>
          openMenu(
            event.currentTarget,
            <CategoryFilter
              selected={cats}
              onApply={(next) => {
                closeLayer();
                setFilter({ cat: next.length ? next.join(',') : null });
              }}
            />,
            { role: 'dialog' },
          )
        }
      >
        <Icon name="tag" className="s" />
        <span>{cats.length === 1 ? CATEGORY[cats[0] as GroupKey]?.label : cats.length ? he.tx.categories(cats.length) : he.tx.category}</span>
        <Icon name="chevD" className="xs" />
      </button>
      <div className="search only-d" style={{ minWidth: '220px' }}>
        <label className="sr-only" htmlFor="tx-q">
          {he.tx.searchPlaceholder}
        </label>
        <Icon name="search" className="s" />
        <input
          className="input"
          id="tx-q"
          type="search"
          inputMode="search"
          placeholder={he.tx.searchPlaceholder}
          defaultValue={query.q ?? ''}
          onChange={(event) => {
            const value = event.target.value.trim();
            window.clearTimeout(searchTimer.current);
            searchTimer.current = window.setTimeout(() => setFilter({ q: value || null }), 250);
          }}
        />
      </div>
      <div className="toggle-group only-d" role="group" aria-label={he.tx.status}>
        {([['', he.tx.statusAll], ['pending', he.tx.pending], ['booked', he.tx.booked]] as const).map(([value, label]) => (
          <button key={value} type="button" aria-pressed={(query.status ?? '') === value} onClick={() => setFilter({ status: value || null })}>
            {label}
          </button>
        ))}
      </div>
      {query.kind === 'returns' ? <Chip tone="warn" icon="repeat">{he.tx.returnsOnly}</Chip> : null}
      {count ? (
        <button type="button" className="btn ghost sm only-d" onClick={() => setFilter({ account: null, cat: null, q: null, dir: null, status: null, kind: null, from: null, to: null })}>
          <Icon name="x" className="xs" />
          {he.tx.clearFilter}
        </button>
      ) : null}
      <button
        type="button"
        className="btn only-m"
        onClick={() =>
          openSheet({
            title: he.tx.filter,
            content: (
              <MobileFilters
                query={query}
                accounts={accounts.map((row) => ({ id: row.account.id, label: `${row.account.name} ${row.account.numberLast4 ? `****${row.account.numberLast4}` : ''}` }))}
                onApply={(patch) => {
                  closeLayer();
                  setFilter(patch);
                }}
              />
            ),
          })
        }
      >
        <Icon name="filter" className="s" />
        {he.tx.filter}
        {count ? <span className="chip accent" style={{ padding: '0 7px' }}>{count}</span> : null}
      </button>
      <button
        type="button"
        className="btn-icon only-m"
        aria-label={he.app.search}
        onClick={() =>
          openSheet({
            title: he.app.search,
            content: (
              <form
                onSubmit={(event) => {
                  event.preventDefault();
                  const value = (event.currentTarget.elements.namedItem('q') as HTMLInputElement).value.trim();
                  closeLayer();
                  setFilter({ q: value || null });
                }}
              >
                <div className="search">
                  <label className="sr-only" htmlFor="tx-qm">
                    {he.tx.searchPlaceholder}
                  </label>
                  <Icon name="search" className="s" />
                  <input className="input" id="tx-qm" name="q" type="search" inputMode="search" placeholder={he.tx.searchPlaceholder} defaultValue={query.q ?? ''} />
                </div>
                <button type="submit" className="btn primary lg block" style={{ marginTop: '12px' }}>
                  {he.app.search}
                </button>
              </form>
            ),
          })
        }
      >
        <Icon name="search" />
      </button>
    </div>
  );
}

function CategoryFilter({ selected, onApply }: { selected: string[]; onApply: (next: string[]) => void }) {
  const [picked, setPicked] = useState<string[]>(selected);
  const [needle, setNeedle] = useState('');
  return (
    <>
      <div style={{ padding: '4px 6px' }}>
        <div className="search">
          <label className="sr-only" htmlFor="catf-q">
            {he.app.search}
          </label>
          <Icon name="search" className="s" />
          <input className="input" id="catf-q" type="search" value={needle} onChange={(event) => setNeedle(event.target.value)} />
        </div>
      </div>
      {GROUP_KEYS.filter((key) => !needle || CATEGORY[key].label.includes(needle)).map((key) => (
        <label className="mi" key={key}>
          <input
            type="checkbox"
            className="check"
            checked={picked.includes(key)}
            aria-label={CATEGORY[key].label}
            onChange={(event) => setPicked((prev) => (event.target.checked ? [...prev, key] : prev.filter((k) => k !== key)))}
          />
          <Icon name={CATEGORY[key].icon} className="s" />
          {CATEGORY[key].label}
        </label>
      ))}
      <hr />
      <div className="row" style={{ padding: '4px 8px 6px', gap: '8px' }}>
        <button type="button" className="btn primary sm" onClick={() => onApply(picked)}>
          {he.app.apply}
        </button>
        <button type="button" className="btn ghost sm" onClick={() => onApply([])}>
          {he.app.clear}
        </button>
      </div>
    </>
  );
}

function MobileFilters({
  query,
  accounts,
  onApply,
}: {
  query: Readonly<Record<string, string>>;
  accounts: Array<{ id: string; label: string }>;
  onApply: (patch: Record<string, string | null>) => void;
}) {
  const [account, setAccount] = useState(query.account ?? '');
  const [cats, setCats] = useState<string[]>(query.cat ? query.cat.split(',') : []);
  const [q, setQ] = useState(query.q ?? '');
  const [status, setStatus] = useState(query.status ?? '');
  return (
    <>
      <div className="field">
        <label htmlFor="fs-acc">{he.tx.account}</label>
        <div className="selwrap" style={{ display: 'block' }}>
          <select className="select" id="fs-acc" value={account} onChange={(event) => setAccount(event.target.value)}>
            <option value="">{he.tx.allAccounts}</option>
            {accounts.map((item) => (
              <option key={item.id} value={item.id}>
                {item.label}
              </option>
            ))}
          </select>
          <Icon name="chevD" className="xs" />
        </div>
      </div>
      <div className="field">
        <span style={{ fontSize: '13px', fontWeight: 500, color: 'var(--text-2)' }}>{he.tx.category}</span>
        <div className="row wrap" style={{ gap: '6px' }}>
          {GROUP_KEYS.map((key) => (
            <button
              key={key}
              type="button"
              className={`chip btn-chip${cats.includes(key) ? ' on' : ''}`}
              aria-pressed={cats.includes(key)}
              onClick={() => setCats((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]))}
            >
              {CATEGORY[key].label}
            </button>
          ))}
        </div>
      </div>
      <div className="field">
        <label htmlFor="fs-q">{he.app.search}</label>
        <input className="input" id="fs-q" type="search" inputMode="search" value={q} onChange={(event) => setQ(event.target.value)} placeholder={he.tx.searchPlaceholder} />
      </div>
      <div className="field">
        <span style={{ fontSize: '13px', fontWeight: 500, color: 'var(--text-2)' }}>{he.tx.status}</span>
        <div className="toggle-group" role="group" aria-label={he.tx.status}>
          {([['', he.tx.statusAll], ['pending', he.tx.pending], ['booked', he.tx.booked]] as const).map(([value, label]) => (
            <button key={value} type="button" aria-pressed={status === value} onClick={() => setStatus(value)}>
              {label}
            </button>
          ))}
        </div>
      </div>
      <div className="row" style={{ gap: '8px', marginTop: '6px' }}>
        <button
          type="button"
          className="btn primary lg grow"
          onClick={() => onApply({ account: account || null, cat: cats.length ? cats.join(',') : null, q: q.trim() || null, status: status || null })}
        >
          {he.tx.show}
        </button>
        <button type="button" className="btn lg" onClick={() => onApply({ account: null, cat: null, q: null, status: null, dir: null, kind: null })}>
          {he.app.clear}
        </button>
      </div>
    </>
  );
}

function SummaryRow({
  response,
  query,
  setFilter,
}: {
  response: TransactionsResponse;
  query: Readonly<Record<string, string>>;
  setFilter: (patch: Record<string, string | null>) => void;
}) {
  const { summary } = response;
  return (
    <div className="sumrow" aria-live="polite">
      {/* §8 "one hero per screen": the filtered total is the answer this screen exists to give. */}
      <div className="stat">
        <span className="l">{he.tx.expenses}</span>
        <span className="v big num">{fmtMoney(summary.debitAgorot)}</span>
      </div>
      <div className="stat">
        <span className="l">{he.tx.incomes}</span>
        <span className="v num">{fmtMoney(summary.creditAgorot)}</span>
      </div>
      <div className="stat">
        <span className="l">{he.tx.count}</span>
        <span className="v">
          <span className="num">{summary.count}</span>
        </span>
        {summary.pendingCount ? (
          <span className="d">{summary.pendingCount === 1 ? he.tx.ofWhichPendingOne : he.tx.ofWhichPendingMany(summary.pendingCount)}</span>
        ) : null}
      </div>
      {query.from && query.to ? (
        <div className="stat">
          <span className="l">{he.tx.range}</span>
          <span className="v" style={{ fontSize: '16px' }}>
            <span className="num">
              {fmtDate(query.from)} – {fmtDate(query.to)}
            </span>
          </span>
          <span className="d">
            <button type="button" className="link" onClick={() => setFilter({ from: null, to: null })}>
              {he.tx.backToPeriod}
            </button>
          </span>
        </div>
      ) : null}
    </div>
  );
}

function TableRows({
  rows,
  dayTotals,
  openId,
  onOpen,
}: {
  rows: TransactionRow[];
  dayTotals: Map<string, { debitAgorot: number }>;
  openId?: string;
  onOpen: (id: string) => void;
}) {
  const { openMenu, openSheet, closeLayer, api, toast } = useApp();
  const isMobile = useIsMobile();
  let lastDay: string | null = null;
  const nodes: ReactNode[] = [];
  function renderRow(row: TransactionRow) {
    return (
      <tr
        key={row.id}
        className={`tx${row.internal || row.scope !== 'spend' && row.scope !== 'income' ? ' internal' : ''}${openId === row.id ? ' on' : ''}`}
        tabIndex={0}
        aria-label={`${row.merchant} ${fmtMoney(row.amountAgorot, 2)}`}
        onClick={() => onOpen(row.id)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onOpen(row.id);
          }
        }}
      >
        <td>
          <span className="num tab">{fmtDM(row.effDate)}</span>
        </td>
        <td>
          <span className="merchant">
            <TxProvider row={row} />
            <TxIcon row={row} size={16} />
            <span className="n">{row.merchant}</span>
            <ForeignLine row={row} />
            {row.note ? <Icon name="note" className="xs t3" /> : null}
          </span>
        </td>
        <td>
          <CategoryChip
            row={row}
            onEdit={(anchor) => {
              const picker = (
                <CategoryPicker
                  row={row}
                  onPick={(groupKey, applyToMerchant) => {
                    closeLayer();
                    api.updateTransaction(row.id, { groupKey, applyToMerchant }).then(
                      (result) =>
                        toast(
                          result.affected > 1
                            ? he.tx.categoryUpdatedN(CATEGORY[groupKey].label, result.affected)
                            : he.tx.categoryUpdated(CATEGORY[groupKey].label),
                        ),
                      () => toast(he.errors.INTERNAL ?? '', { error: true }),
                    );
                  }}
                />
              );
              if (isMobile) openSheet({ title: `${he.tx.changeCategory} — ${row.merchant}`, content: <div className="menu" style={{ position: 'static', boxShadow: 'none', border: 0, padding: 0 }}>{picker}</div> });
              else openMenu(anchor, picker);
            }}
          />
        </td>
        <td>
          <span className="num tab">{row.installment ? `${row.installment.n}/${row.installment.total}` : ''}</span>
        </td>
        <td className="amt">
          <Amount row={row} />
        </td>
        <td>
          <PendingChip row={row} />
        </td>
      </tr>
    );
  }
  for (const row of rows) {
    if (row.effDate !== lastDay) {
      lastDay = row.effDate;
      const total = dayTotals.get(row.effDate);
      nodes.push(
        <tr className="day" key={`day-${row.effDate}`}>
          <th colSpan={6} scope="colgroup">
            {dayName(row.effDate)}, <span className="num tab">{fmtDate(row.effDate)}</span> ·{' '}
            <span className="num tab">{fmtMoney(-(total?.debitAgorot ?? 0), 2)}</span>
          </th>
        </tr>,
      );
    }
    nodes.push(renderRow(row));
  }
  return <>{nodes}</>;
}

function ListRows({ rows, dayTotals, onOpen }: { rows: TransactionRow[]; dayTotals: Map<string, { debitAgorot: number }>; onOpen: (id: string) => void }) {
  const nodes: ReactNode[] = [];
  let lastDay: string | null = null;
  for (const row of rows) {
    if (row.effDate !== lastDay) {
      lastDay = row.effDate;
      const total = dayTotals.get(row.effDate);
      nodes.push(
        <div className="dayh" key={`day-${row.effDate}`}>
          <span>
            {dayName(row.effDate)}, <span className="num tab">{fmtDate(row.effDate)}</span>
          </span>
          <span className="num tab">{fmtMoney(-(total?.debitAgorot ?? 0), 2)}</span>
        </div>,
      );
    }
    nodes.push(<MobileRow key={row.id} row={row} onOpen={onOpen} />);
  }
  return <>{nodes}</>;
}

function MobileRow({ row, onOpen }: { row: TransactionRow; onOpen: (id: string) => void }) {
  const { api, toast, openSheet, closeLayer } = useApp();
  const innerRef = useRef<HTMLDivElement | null>(null);
  const startX = useRef<number | null>(null);
  const moved = useRef(false);
  const category = row.scope === 'income' ? he.tx.incomeChip : row.returnKind ? he.tx.returnChip : row.internal ? he.tx.internalChip : CATEGORY[row.groupKey]?.label;

  const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.pointerType === 'mouse') return;
    startX.current = event.clientX;
    moved.current = false;
    if (innerRef.current) innerRef.current.style.transition = 'none';
  };
  const onPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (startX.current === null || !innerRef.current) return;
    let dx = event.clientX - startX.current;
    if (Math.abs(dx) > 8) moved.current = true;
    dx = Math.max(-176, Math.min(96, dx));
    innerRef.current.style.transform = `translateX(${dx}px)`;
  };
  const onPointerUp = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (startX.current === null || !innerRef.current) return;
    const dx = event.clientX - startX.current;
    innerRef.current.style.transition = '';
    innerRef.current.style.transform = dx < -60 ? 'translateX(-176px)' : dx > 60 ? 'translateX(96px)' : '';
    startX.current = null;
  };

  return (
    <div className={`txm${row.internal ? ' internal' : ''}`}>
      <div className="acts l">
        <button
          type="button"
          className="a"
          onClick={() =>
            openSheet({
              title: `${he.tx.changeCategory} — ${row.merchant}`,
              content: (
                <div className="menu" style={{ position: 'static', boxShadow: 'none', border: 0, padding: 0 }}>
                  <CategoryPicker
                    row={row}
                    onPick={(groupKey, applyToMerchant) => {
                      closeLayer();
                      void api.updateTransaction(row.id, { groupKey, applyToMerchant }).then((result) =>
                        toast(result.affected > 1 ? he.tx.categoryUpdatedN(CATEGORY[groupKey].label, result.affected) : he.tx.categoryUpdated(CATEGORY[groupKey].label)),
                      );
                    }}
                  />
                </div>
              ),
            })
          }
        >
          <Icon name="tag" className="s" />
          {he.tx.changeCategory}
        </button>
        <button
          type="button"
          className="g"
          onClick={() => {
            void api.updateTransaction(row.id, { internal: !row.internal });
            toast(row.internal ? he.tx.unmarkedInternal : he.tx.markedInternal);
          }}
        >
          <Icon name="repeat" className="s" />
          {row.internal ? he.tx.unmarkInternalShort : he.tx.markInternalShort}
        </button>
      </div>
      <div className="acts r">
        <button
          type="button"
          onClick={() =>
            openSheet({
              title: `${he.tx.note} — ${row.merchant}`,
              content: (
                <form
                  onSubmit={(event) => {
                    event.preventDefault();
                    const value = (event.currentTarget.elements.namedItem('note') as HTMLTextAreaElement).value;
                    closeLayer();
                    void api.updateTransaction(row.id, { note: value || null }).then(() => toast(he.tx.noteSaved));
                  }}
                >
                  <div className="field">
                    <label htmlFor="tx-note-m">{he.tx.note}</label>
                    <textarea className="input" id="tx-note-m" name="note" placeholder={he.tx.notePlaceholder} defaultValue={row.note ?? ''} />
                  </div>
                  <button type="submit" className="btn primary lg block" style={{ marginTop: '12px' }}>
                    {he.app.save}
                  </button>
                </form>
              ),
            })
          }
        >
          <Icon name="note" className="s" />
          {he.tx.addNote}
        </button>
      </div>
      <div
        ref={innerRef}
        className="inner"
        role="button"
        tabIndex={0}
        aria-label={`${row.merchant} ${fmtMoney(row.amountAgorot, 2)}`}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onClick={() => {
          if (moved.current) return;
          onOpen(row.id);
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onOpen(row.id);
          }
        }}
      >
        <TxIcon row={row} size={22} />
        <span className="main" style={{ flex: 1, minWidth: 0 }}>
          <span className="name" style={{ display: 'block', fontWeight: 500, fontSize: '14px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {row.merchant}
          </span>
          <span className="meta">
            {category}
            {row.installment ? ` · תשלום ${row.installment.n}/${row.installment.total}` : ''} · {row.account.name}
          </span>
        </span>
        <span className="amt" style={{ textAlign: 'end', fontSize: '15px', fontWeight: 600 }}>
          <Amount row={row} />
          {row.status === 'PENDING' ? (
            <>
              <br />
              <PendingChip row={row} />
            </>
          ) : null}
        </span>
      </div>
    </div>
  );
}

function CategoryPicker({ row, onPick }: { row: TransactionRow; onPick: (groupKey: GroupKey, applyToMerchant: boolean) => void }) {
  const [applyAll, setApplyAll] = useState(true);
  const [needle, setNeedle] = useState('');
  return (
    <>
      <div style={{ padding: '4px 6px' }}>
        <div className="search">
          <label className="sr-only" htmlFor="catp-q">
            {he.app.search}
          </label>
          <Icon name="search" className="s" />
          <input className="input" id="catp-q" type="search" value={needle} onChange={(event) => setNeedle(event.target.value)} />
        </div>
      </div>
      {GROUP_KEYS.filter((key) => !needle || CATEGORY[key].label.includes(needle)).map((key) => (
        <button key={key} type="button" className="mi" role="menuitemradio" aria-checked={key === row.groupKey} onClick={() => onPick(key, applyAll)}>
          <Icon name={CATEGORY[key].icon} className="s" />
          {CATEGORY[key].label}
          <Icon name="check" className="chk" />
        </button>
      ))}
      <hr />
      <label className="check" style={{ padding: '6px 12px 8px' }}>
        <input type="checkbox" checked={applyAll} onChange={(event) => setApplyAll(event.target.checked)} />
        {he.tx.applyToMerchant(row.merchant)}
      </label>
    </>
  );
}

function DetailPanel({ detail, onClose, onChanged }: { detail: TransactionDetail; onClose: () => void; onChanged: (next: TransactionDetail) => void }) {
  const { api, toast } = useApp();
  const [note, setNote] = useState(detail.note ?? '');
  useEffect(() => setNote(detail.note ?? ''), [detail.id, detail.note]);
  return (
    <aside className="detail open" role="dialog" aria-modal="false" aria-label={he.tx.detailLabel}>
      <div className="dh">
        <h2 style={{ fontSize: '17px' }}>{detail.merchant}</h2>
        <button type="button" className="btn-icon" aria-label={he.app.close} onClick={onClose}>
          <Icon name="x" />
        </button>
      </div>
      <div className="db">
        <div className="stat">
          {/* The drawer overlays the screen, so its amount stays on the 22px step — the screen's
              one hero is the filtered total behind it (§8). Weight, not size, sets it apart. */}
          <span className="v" style={{ fontWeight: 700 }}>
            <Amount row={detail} />
          </span>
          {detail.origCurrency && detail.origCurrency !== detail.currency ? (
            <span className="d">{fmtForeign(detail.origAmountMinor, detail.origCurrency)}</span>
          ) : null}
        </div>
        <dl className="kv">
          <dt>{he.tx.txDate}</dt>
          <dd>
            <span className="num">{fmtDate(detail.txDate ?? detail.effDate)}</span>
          </dd>
          <dt>{he.tx.booking}</dt>
          <dd>{detail.bookingDate ? <span className="num">{fmtDate(detail.bookingDate)}</span> : <Chip tone="warn" icon="clock">{he.tx.awaitingBooking}</Chip>}</dd>
          <dt>{he.tx.accountLabel}</dt>
          <dd>
            <Ltr>{`${detail.account.name} ${detail.account.numberLast4 ? `****${detail.account.numberLast4}` : ''}`}</Ltr>{' '}
            {detail.account.dataAsOf ? (
              <span className="tag critical">
                <Icon name="alert" className="xs" />
                נכון ל-{fmtDate(detail.account.dataAsOf.slice(0, 10))}
              </span>
            ) : null}
          </dd>
          <dt>{he.tx.colCategory}</dt>
          <dd>
            <CategoryChip row={detail} />
            {detail.userCategory ? (
              <span className="meta" style={{ display: 'block' }}>
                {he.tx.changedBy(detail.userCategory.by, fmtDM(detail.userCategory.at.slice(0, 10)))}
              </span>
            ) : null}
          </dd>
          {detail.installmentPlan ? (
            <>
              <dt>{he.tx.colInstallments}</dt>
              <dd>
                <span className="num">{he.tx.installmentsOf(detail.installmentPlan.postedCount, detail.installmentPlan.totalCount)}</span>
                {detail.installmentPlan.remainingCount
                  ? ` · ${he.tx.installmentsLeft(detail.installmentPlan.remainingCount, fmtMoney(detail.installmentPlan.amountAgorot, 2))}`
                  : ` · ${he.tx.installmentsLast}`}
                {detail.installmentPlan.totalAgorot ? (
                  <span className="meta" style={{ display: 'block' }}>
                    {he.tx.installmentsTotal(fmtMoney(detail.installmentPlan.totalAgorot, 2))}
                  </span>
                ) : null}
              </dd>
            </>
          ) : null}
          {detail.balanceEndDayAgorot !== null ? (
            <>
              <dt>{detail.account.type === 'CARD' ? he.tx.balanceAfterCard : he.tx.balanceAfterChecking}</dt>
              <dd>
                <span className="num">{fmtMoney(detail.balanceEndDayAgorot)}</span>
              </dd>
            </>
          ) : null}
        </dl>
        <div className="field">
          <label htmlFor="tx-note">{he.tx.note}</label>
          <textarea
            className="input"
            id="tx-note"
            placeholder={he.tx.notePlaceholder}
            value={note}
            onChange={(event) => setNote(event.target.value)}
            onBlur={() => {
              if ((detail.note ?? '') === note) return;
              api.updateTransaction(detail.id, { note: note || null }).then((result) => onChanged(result.item));
            }}
          />
        </div>
        {detail.returnKind ? <div className="notice warn"><Icon name="alert" /><span>{he.tx.returnNotice}</span></div> : null}
        {detail.scope === 'card-charge' ? <div className="notice info"><Icon name="info" /><span>{he.tx.internalNoticeCard}</span></div> : null}
        {detail.scope === 'transfer' ? <div className="notice info"><Icon name="info" /><span>{he.tx.internalNoticeTransfer}</span></div> : null}
        {detail.scope !== 'income' && !detail.returnKind ? (
          <div className="row wrap">
            <button
              type="button"
              className="btn"
              onClick={() =>
                api.updateTransaction(detail.id, { internal: !detail.internal }).then((result) => {
                  onChanged(result.item);
                  toast(detail.internal ? he.tx.unmarkedInternal : he.tx.markedInternal);
                })
              }
            >
              <Icon name="repeat" className="s" />
              {detail.internal ? he.tx.unmarkInternal : he.tx.markInternal}
            </button>
          </div>
        ) : null}
      </div>
    </aside>
  );
}
